import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { myPagePath } from '../utils/myPage';
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import BetaForm from '../BetaForm';
import LandingNav from '../components/LandingNav';
import LandingFooter from '../components/LandingFooter';
import { API } from '../utils/constants';
import PasswordInput from '../components/PasswordInput';

const LandingPage = ({ inviteLookupPending = false, verifiedInvite = null }) => {
  const location = useLocation();
  // Capture before scrubbing browser history. An invitation is a signup intent,
  // not permission to resume (or authenticate) another browser account.
  const invitedSignup = React.useRef(verifiedInvite || location.state?.verifiedInvite || null);
  const explicitSignup = React.useRef(
    Boolean(invitedSignup.current) ||
    new URLSearchParams(window.location.search).get('signup') === 'true' ||
    inviteLookupPending
  );
  const [adminToken, setAdminToken] = useState(null);
  const signupDraft = invitedSignup.current && location.state?.signupDraft;
  const [formData, setFormData] = useState({
    firstName: typeof signupDraft?.firstName === 'string' ? signupDraft.firstName : "",
    lastName: typeof signupDraft?.lastName === 'string' ? signupDraft.lastName : "",
    email: "",
    username: typeof signupDraft?.username === 'string' ? signupDraft.username : "",
    password: "",
    confirmPassword: "",
    accessCode: ""
  });
  const [usernameStatus, setUsernameStatus] = useState({ state: "idle", reason: null });
  const [showChooseHandleDialog, setShowChooseHandleDialog] = useState(false);
  const [savingHandle, setSavingHandle] = useState(false);
  const [loginData, setLoginData] = useState({
    email: "",
    password: ""
  });
  const [showSignUpDialog, setShowSignUpDialog] = useState(inviteLookupPending);
  const [showLoginDialog, setShowLoginDialog] = useState(Boolean(invitedSignup.current && location.state?.signupLoginRequested));
  const [showBetaDialog, setShowBetaDialog] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [showForgotPasswordDialog, setShowForgotPasswordDialog] = useState(false);
  const [showResetPasswordDialog, setShowResetPasswordDialog] = useState(false);
  const [showVerificationDialog, setShowVerificationDialog] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState("");
  // Stable invitations hold an unverified signup attempt in memory until the
  // recipient proves inbox ownership. Never put its secret in browser storage.
  const [verificationId, setVerificationId] = useState(null);
  const [verificationDeliveryFailed, setVerificationDeliveryFailed] = useState(false);
  const signupPending = React.useRef(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [resetPasswordData, setResetPasswordData] = useState({ token: "", password: "", confirmPassword: "" });
  const [isDesktop, setIsDesktop] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [showStickyNav, setShowStickyNav] = useState(false);
  const [googleClientId, setGoogleClientId] = useState(null);
  const [signUpStep, setSignUpStep] = useState(inviteLookupPending ? 2 : 1);
  // True for both legacy tokens and stable references; the email is locked.
  // A stable reference alone never authorizes access to an account.
  const [inviteMode, setInviteMode] = useState(false);
  const openSignUpDialog = () => {
    setShowSignUpDialog(true);
  };
  const navigate = useNavigate();
  const founderSectionRef = React.useRef(null);
  const heroRef = React.useRef(null);
  const accordionSectionRef = React.useRef(null);
  const accordionItemsRef = React.useRef(null);
  const [accordionVisible, setAccordionVisible] = useState(false);
  // Where to land after a successful login (defaults to your own page)
  const loginReturnToRef = React.useRef(null);
  const googleLoginBtnRef = React.useRef(null);
  const googleSignupBtnRef = React.useRef(null);
  const hiddenGoogleBtnRef = React.useRef(null);
  const hiddenGoogleLoginBtnRef = React.useRef(null);
  const [activeAccordionItem, setActiveAccordionItem] = useState(0);
  const [wordRevealProgress, setWordRevealProgress] = useState(0);
  const [categoryScrollOffset, setCategoryScrollOffset] = useState(0);
  const wordRevealRef = React.useRef(null);
  const categoryPillsRef = React.useRef(null);

  // Detect screen sizes
  useEffect(() => {
    const checkScreenSize = () => {
      setIsDesktop(window.innerWidth >= 1024);
      setIsMobile(window.innerWidth < 768);
    };
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Redirect to /recipes if user is already logged in (unless ?preview=true)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const isPreview = urlParams.get('preview') === 'true';
    const isLoginRequest = urlParams.get('login') === 'true';
    
    if (isLoginRequest) {
      // e.g. "Already have an account? Log in" from the save-a-recipe
      // waitlist form: after login, return to the page they came from.
      const rt = urlParams.get('returnTo');
      // Same-origin only: reject "//attacker.com", "/\attacker.com", etc.
      if (rt) {
        try {
          const parsed = new URL(rt, window.location.origin);
          if (parsed.origin === window.location.origin && !rt.includes('\\') && !rt.startsWith('//')) {
            loginReturnToRef.current = parsed.pathname + parsed.search + parsed.hash;
          }
        } catch {
          // malformed — ignore, fall back to own page after login
        }
      }
      setShowLoginDialog(true);
      window.history.replaceState({}, '', '/');
    }
    if (explicitSignup.current) {
      const inviteToken = invitedSignup.current?.code || urlParams.get('invite');
      const inviteEmail = invitedSignup.current?.email || urlParams.get('invite_email');
      if (!inviteLookupPending && inviteToken && inviteEmail) {
        invitedSignup.current = { code: inviteToken, email: inviteEmail };
        // Arrived via an email invitation link: skip the code step entirely.
        setInviteMode(true);
        setFormData(prev => ({ ...prev, accessCode: inviteToken, email: inviteEmail }));
        setSignUpStep(2);
      }
      if (!location.state?.signupDismissed) setShowSignUpDialog(true);
      if (!inviteLookupPending) window.history.replaceState({}, '', '/');
    }

    if (!isPreview) {
      const token = localStorage.getItem('adminToken');
      // An explicit login request can come from a private-recipe screen while
      // somebody is signed in to the wrong account. Keep the login dialog open
      // so they can switch accounts instead of bouncing straight back.
      if (token && !isLoginRequest && !explicitSignup.current) {
        navigate(loginReturnToRef.current || myPagePath());
      }
    }
  }, [navigate]);

  // InvitePage keeps this component mounted while lookup completes. Apply only
  // server-validated details, without reopening a dismissed dialog or replacing
  // local account/login drafts. Keep the invitation out of browser history.
  useEffect(() => {
    if (!verifiedInvite) return;
    invitedSignup.current = verifiedInvite;
    setInviteMode(true);
    setFormData(prev => ({ ...prev, accessCode: verifiedInvite.code, email: verifiedInvite.email }));
    setSignUpStep(2);
    window.history.replaceState({}, '', '/');
  }, [verifiedInvite]);

  const [googleReady, setGoogleReady] = useState(false);

  useEffect(() => {
    axios.get(`${API}/auth/google-client-id`).then(res => {
      if (res.data.client_id) setGoogleClientId(res.data.client_id);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!googleClientId) return;
    const checkGoogle = () => {
      if (window.google && window.google.accounts) {
        setGoogleReady(true);
      }
    };
    checkGoogle();
    if (!googleReady) {
      const interval = setInterval(() => {
        checkGoogle();
      }, 200);
      return () => clearInterval(interval);
    }
  }, [googleClientId, googleReady]);

  const handleGoogleResponseRef = React.useRef(null);
  handleGoogleResponseRef.current = async (response) => {
    if (inviteLookupPending) return;
    try {
      const res = await axios.post(`${API}/auth/google`, {
        credential: response.credential,
        access_code: showSignUpDialog
          ? (invitedSignup.current?.code || formData.accessCode || undefined)
          : undefined,
      });
      if (res.data.success) {
        setAdminToken(res.data.token);
        localStorage.setItem('adminToken', res.data.token);
        if (res.data.refresh_token) localStorage.setItem('refreshToken', res.data.refresh_token);
        localStorage.setItem('user', JSON.stringify(res.data.user));
        setShowSignUpDialog(false);
        setShowLoginDialog(false);
        if (res.data.needs_username) {
          // Brand-new Google account: they must pick their handle before
          // entering the app (the stored one is just a placeholder).
          setFormData(prev => ({ ...prev, username: "" }));
          setShowChooseHandleDialog(true);
        } else {
          navigate(loginReturnToRef.current || myPagePath());
        }
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Google sign-in failed";
      toast.error(errorMsg);
    }
  };

  const initGoogleAuth = () => {
    if (!googleClientId || !googleReady) return;
    window.google.accounts.id.initialize({
      client_id: googleClientId,
      callback: (resp) => handleGoogleResponseRef.current(resp),
    });
  };

  const renderGoogleButton = (containerRef) => {
    if (!googleClientId || !googleReady || !containerRef.current) return;
    containerRef.current.innerHTML = '';
    initGoogleAuth();
    window.google.accounts.id.renderButton(containerRef.current, {
      theme: 'outline',
      size: 'large',
      width: containerRef.current.offsetWidth || 300,
      text: 'continue_with',
    });
  };

  const renderHiddenGoogleButton = () => {
    if (!googleClientId || !googleReady || !hiddenGoogleBtnRef.current) return;
    hiddenGoogleBtnRef.current.innerHTML = '';
    initGoogleAuth();
    window.google.accounts.id.renderButton(hiddenGoogleBtnRef.current, {
      theme: 'outline',
      size: 'large',
      width: 300,
      text: 'signup_with',
    });
  };

  const triggerHiddenGoogleBtn = (ref) => {
    if (!ref.current) return;
    const btn = ref.current.querySelector('[role="button"]') ||
                ref.current.querySelector('div[aria-labelledby]') ||
                ref.current.querySelector('iframe');
    if (btn) btn.click();
  };

  useEffect(() => {
    if (showLoginDialog && googleReady) {
      const timer = setTimeout(() => {
        if (!hiddenGoogleLoginBtnRef.current) return;
        hiddenGoogleLoginBtnRef.current.innerHTML = '';
        initGoogleAuth();
        window.google.accounts.id.renderButton(hiddenGoogleLoginBtnRef.current, {
          theme: 'outline', size: 'large', width: 300, text: 'signin_with',
        });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [showLoginDialog, googleReady]);

  useEffect(() => {
    if (showSignUpDialog && signUpStep === 2 && googleReady && !inviteLookupPending) {
      const timer = setTimeout(() => renderHiddenGoogleButton(), 100);
      return () => clearTimeout(timer);
    }
  }, [showSignUpDialog, signUpStep, googleReady, inviteLookupPending]);

  // Scroll animation for founder section
  useEffect(() => {
    const handleScroll = () => {
      // Check if scrolled past hero section (20% earlier)
      if (heroRef.current) {
        const heroRect = heroRef.current.getBoundingClientRect();
        const heroHeight = heroRef.current.offsetHeight;
        // Show nav when 80% of hero is scrolled past (20% earlier than before)
        setShowStickyNav(heroRect.bottom < (heroHeight * 0.2));
      }

      if (founderSectionRef.current) {
        const section = founderSectionRef.current;
        const rect = section.getBoundingClientRect();
        const sectionHeight = section.offsetHeight;
        const windowHeight = window.innerHeight;
        
        // Calculate scroll progress within the section (0 to 1)
        // Reduced the divisor to speed up the animation (was + 300, now + 100)
        const scrollTop = -rect.top;
        const progress = Math.max(0, Math.min(1, scrollTop / (sectionHeight - windowHeight + 100)));
        setScrollProgress(progress);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial call
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    let ticking = false;
    const handleNewSectionsScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        if (wordRevealRef.current) {
          const rect = wordRevealRef.current.getBoundingClientRect();
          const windowHeight = window.innerHeight;
          const progress = Math.max(0, Math.min(1, (windowHeight - rect.top) / (windowHeight + rect.height)));
          setWordRevealProgress(progress);
        }
        if (categoryPillsRef.current) {
          const rect = categoryPillsRef.current.getBoundingClientRect();
          const windowHeight = window.innerHeight;
          const offset = Math.max(0, Math.min(1, (windowHeight - rect.top) / (windowHeight + rect.height)));
          setCategoryScrollOffset(offset);
        }
        ticking = false;
      });
    };
    window.addEventListener('scroll', handleNewSectionsScroll, { passive: true });
    handleNewSectionsScroll();
    return () => window.removeEventListener('scroll', handleNewSectionsScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        // Track visibility continuously so the auto-rotation below can pause
        // whenever the accordion is off screen — otherwise its height changes
        // keep shifting the page while the user reads sections further down.
        setAccordionInView(entry.isIntersecting);
        if (entry.isIntersecting && !accordionVisible) {
          setAccordionVisible(true);
          setActiveAccordionItem(0);
        }
      },
      { threshold: 0.2 }
    );
    if (accordionItemsRef.current) observer.observe(accordionItemsRef.current);
    return () => observer.disconnect();
  }, [accordionVisible]);

  const [accordionTimerKey, setAccordionTimerKey] = useState(0);
  const [accordionInView, setAccordionInView] = useState(false);

  useEffect(() => {
    // Only auto-advance while the accordion is actually on screen.
    if (!accordionVisible || !accordionInView) return;
    const interval = setInterval(() => {
      setActiveAccordionItem(prev => (prev + 1) % 5);
    }, 5000);
    return () => clearInterval(interval);
  }, [accordionVisible, accordionInView, accordionTimerKey]);

  const USERNAME_RE = /^[a-z0-9_](?:[a-z0-9_-]{1,28}[a-z0-9_])?$/;

  // Debounced live "is this handle free?" check while typing.
  useEffect(() => {
    const handle = (formData.username || "").trim().toLowerCase();
    if (!handle) {
      setUsernameStatus({ state: "idle", reason: null });
      return;
    }
    if (handle.length < 3 || handle.length > 30 || !USERNAME_RE.test(handle)) {
      setUsernameStatus({
        state: "invalid",
        reason: "Use 3-30 lowercase letters, numbers, underscores, or hyphens",
      });
      return;
    }
    setUsernameStatus({ state: "checking", reason: null });
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/username-available`, { params: { username: handle } });
        if (cancelled) return;
        if (res.data.available) {
          setUsernameStatus({ state: "available", reason: null });
        } else {
          setUsernameStatus({ state: "taken", reason: res.data.reason || "That username is already taken." });
        }
      } catch {
        if (!cancelled) setUsernameStatus({ state: "idle", reason: null });
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [formData.username]);

  const handleSaveChosenHandle = async () => {
    const handle = (formData.username || "").trim().toLowerCase();
    if (!handle || usernameStatus.state === "invalid" || usernameStatus.state === "taken" || usernameStatus.state === "checking") return;
    setSavingHandle(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.put(`${API}/profile`, { username: handle }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      localStorage.setItem('user', JSON.stringify({ ...storedUser, username: res.data.username || handle }));
      setShowChooseHandleDialog(false);
      navigate(myPagePath());
    } catch (error) {
      toast.error(error.response?.data?.detail || "Couldn't save that username. Please try another.");
    } finally {
      setSavingHandle(false);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    if (inviteLookupPending) return;
    if (signupPending.current) return;
    
    // Validate form
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.username || !formData.password || !formData.confirmPassword || !formData.accessCode) {
      toast.error("Please fill in all fields");
      return;
    }

    if (usernameStatus.state === "invalid" || usernameStatus.state === "taken") {
      toast.error(usernameStatus.reason || "Please choose a different username");
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    
    signupPending.current = true;
    try {
      const response = await axios.post(`${API}/register`, {
        email: invitedSignup.current?.email || formData.email,
        first_name: formData.firstName,
        last_name: formData.lastName,
        username: formData.username.trim().toLowerCase(),
        password: formData.password,
        access_code: invitedSignup.current?.code || formData.accessCode
      });
      
      if (response.data.requires_verification) {
        setShowSignUpDialog(false);
        setVerificationEmail(response.data.email);
        setVerificationId(response.data.verification_id || null);
        setVerificationDeliveryFailed(response.data.success === false);
        if (response.data.success === false) {
          toast.error(response.data.message || "Could not send your code. Please use Resend to try again.");
        }
        setVerificationCode("");
        setShowVerificationDialog(true);
      } else if (response.data.success) {
        setAdminToken(response.data.token);
        localStorage.setItem('adminToken', response.data.token);
        if (response.data.refresh_token) localStorage.setItem('refreshToken', response.data.refresh_token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        setShowSignUpDialog(false);
        navigate(myPagePath());
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Registration failed";
      toast.error(errorMsg);
    } finally {
      signupPending.current = false;
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error("Please enter the 6-digit code");
      return;
    }
    
    try {
      const response = await axios.post(`${API}/verify-code`, {
        email: verificationEmail,
        code: verificationCode,
        ...(verificationId ? { verification_id: verificationId } : {})
      });
      
      if (response.data.success) {
        setAdminToken(response.data.token);
        localStorage.setItem('adminToken', response.data.token);
        if (response.data.refresh_token) localStorage.setItem('refreshToken', response.data.refresh_token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        setShowVerificationDialog(false);
        setVerificationId(null);
        toast.success("Email verified!");
        navigate(myPagePath());
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Invalid code";
      toast.error(errorMsg);
    }
  };

  const handleResendCode = async () => {
    try {
      await axios.post(`${API}/resend-verification`, {
        email: verificationEmail,
        ...(verificationId ? { verification_id: verificationId } : {})
      });
      setVerificationDeliveryFailed(false);
      toast.success("New code sent!");
    } catch (error) {
      toast.error("Failed to resend code");
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!loginData.email || !loginData.password) {
      toast.error("Please fill in all fields");
      return;
    }
    
    try {
      const response = await axios.post(`${API}/login`, {
        email: loginData.email,
        password: loginData.password
      });
      
      if (response.data.success) {
        setAdminToken(response.data.token);
        localStorage.setItem('adminToken', response.data.token);
        if (response.data.refresh_token) localStorage.setItem('refreshToken', response.data.refresh_token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        setShowLoginDialog(false);
        navigate(loginReturnToRef.current || myPagePath());
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Login failed";
      
      // Check if it's an email verification issue
      if (error.response?.status === 403 && errorMsg.includes("verify your email")) {
        toast.error(
          <div>
            <p>{errorMsg}</p>
            <button 
              onClick={() => handleResendVerification(loginData.email)}
              style={{ textDecoration: 'underline', marginTop: '8px' }}
            >
              Resend verification email
            </button>
          </div>,
          { duration: 8000 }
        );
      } else {
        toast.error(errorMsg);
      }
    }
  };

  const handleResendVerification = async (email) => {
    try {
      await axios.post(`${API}/resend-verification`, { email });
      toast.success("Verification email sent! Check your inbox.");
    } catch (error) {
      toast.error("Failed to send verification email");
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    
    if (!forgotPasswordEmail) {
      toast.error("Please enter your email");
      return;
    }
    
    try {
      const response = await axios.post(`${API}/auth/forgot-password`, {
        email: forgotPasswordEmail
      });
      
      if (response.data.success) {
        toast.success("Password reset email sent! Please check your inbox.");
        setShowForgotPasswordDialog(false);
        setForgotPasswordEmail("");
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Failed to send reset email";
      toast.error(errorMsg);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (!resetPasswordData.password || !resetPasswordData.confirmPassword) {
      toast.error("Please fill in all fields");
      return;
    }
    
    if (resetPasswordData.password !== resetPasswordData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    
    try {
      const response = await axios.post(`${API}/auth/reset-password`, {
        token: resetPasswordData.token,
        new_password: resetPasswordData.password
      });
      
      if (response.data.success) {
        toast.success("Password reset successfully! You can now login.");
        setShowResetPasswordDialog(false);
        setResetPasswordData({ token: "", password: "", confirmPassword: "" });
        setShowLoginDialog(true);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Failed to reset password";
      toast.error(errorMsg);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      setAdminToken(token);
    }
  }, []);

  const cardBackgrounds = [
    '/borders/card-1.png',
    '/borders/card-2.png',
    '/borders/card-3.png',
    '/borders/card-4.png',
    '/borders/card-5.png',
  ];

  const recipeCards = [
    { text: "I make it, love it, then forget where\u00A0I\u00A0found\u00A0it", rotate: '-2deg', bg: 0 },
    { text: "Post-it notes. In random recipe books.\u00A0Somewhere.", rotate: '2deg', bg: 1 },
    { text: "My head. Which\u00A0is\u00A0ridiculous.", rotate: '-1deg', bg: 2 },
    { text: "I've been meaning to organize this for\u00A0a\u00A0decade", rotate: '1.5deg', bg: 4 },
    { text: "A journal I\u00A0never\u00A0update", rotate: '-2deg', bg: 3 },
  ];

  const accordionItems = [
    { title: "Reduce the mental load", content: "Stop reinventing the wheel every week. Keep your go-to meals in one place so planning what to cook becomes\u00A0simple.", image: "/homepage-reduce-mental-load.png" },
    { title: "Add recipes in seconds", content: "Paste a blog link, speak the recipe out loud, or upload a photo from\u00A0a\u00A0cookbook.", image: "/homepage-add-recipes.png" },
    { title: "Organize recipes your way", content: "Create categories that match your life\u00A0— weeknight dinners, postpartum prep, toddler favorites, dinner\u00A0parties.", image: "/homepage-organize-recipes.png" },
    { title: "Share recipes easily", content: "Send a recipe to your partner, your friend, or your audience with\u00A0one\u00A0link.", image: "/homepage-share-recipes.png" },
    { title: "Delegate cooking", content: "When recipes live in Wellby, anyone in your household can\u00A0cook\u00A0them.", image: "/homepage-delegate-cooking.png" },
  ];

  const revealLines = [
    "We cook thousands of meals over the years.",
    "But most of those recipes slowly disappear,",
    "making every week feel like you're reinventing",
    "the wheel. And when you want to share a recipe?",
    "It feels like a daunting task. Wellby changes that.",
  ];
  const revealWords = revealLines.flatMap((line, lineIdx) =>
    line.split(' ').map((word, wordIdx) => ({ word, lineIdx, isFirst: lineIdx === 0 }))
  );
  const totalRevealWords = revealWords.length;
  const adjustedRevealProgress = Math.max(0, Math.min(1, (wordRevealProgress - 0.15) * 1.8));
  const revealedWordCount = Math.floor(adjustedRevealProgress * totalRevealWords);

  const topRowPills = ["Weeknight dinners", "Baby food", "Postpartum", "on-the-go", "kid snacks", "freezer meals", "dog treats"];
  const bottomRowPills = ["hidden veggie", "Office lunch", "School lunch", "Mila's favs", "hormone balance", "high protein", "dump and bake", "15 min meals", "dinner party"];

  return (
    <div className="min-h-screen relative" style={{ backgroundColor: '#EEE7D8' }}>
      <style>{`
        @keyframes carousel-scroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-33.333%); }
        }
        .recipe-carousel-track {
          animation: carousel-scroll 35s linear infinite;
        }
        .recipe-carousel-track:hover {
          animation-play-state: paused;
        }
        @media (prefers-reduced-motion: reduce) {
          .recipe-carousel-track {
            animation: none;
          }
        }
      `}</style>
      <LandingNav
        onLogin={() => setShowLoginDialog(true)}
        onSignUp={openSignUpDialog}
        onJoinWaitlist={() => setShowBetaDialog(true)}
      />

      {/* Main Content Area */}
      <div ref={heroRef} className="relative" style={{ marginTop: '0px', paddingTop: isMobile ? '80px' : '90px' }}>
        <div className="relative w-full h-full flex items-center justify-center" style={{ paddingTop: isMobile ? '0px' : '24px', paddingBottom: isMobile ? '62px' : '24px', marginTop: isMobile ? '-18%' : '0px' }}>
          
          {/* Decorative elements container - tablet and desktop */}
          <div className="hidden md:block absolute inset-0">
            {/* Eggplant Sticky Note - LEFT SIDE */}
            <div 
              className="absolute z-20"
              style={{
                left: isDesktop ? 'calc(50% - 590px)' : 'calc(50% - 350px)',
                top: isDesktop ? 'calc(50% - 290px)' : 'calc(50% - 230px)',
                width: isDesktop ? '270px' : '200px',
                height: isDesktop ? '270px' : '200px',
                backgroundImage: `url('/eggplant.png')`,
                backgroundSize: 'contain',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
                transform: isDesktop ? 'rotate(-6deg)' : 'rotate(4deg)'
              }}
            ></div>

            {/* Candles - LEFT SIDE */}
            <div 
              className="absolute"
              style={{
                left: isDesktop ? 'calc(50% - 555px)' : 'calc(50% - 365px)',
                top: isDesktop ? 'calc(50% - 50px)' : 'calc(50% + 40px)',
                width: isDesktop ? '250px' : '188px',
                height: isDesktop ? '250px' : '188px',
                backgroundImage: `url('https://customer-assets.emergentagent.com/job_culinary-notes-5/artifacts/hi39abfs_candle.png')`,
                backgroundSize: 'contain',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
                zIndex: 25
              }}
            ></div>

            {/* Desktop/Tablet: Polaroid Frame - RIGHT SIDE */}
            {!isMobile && (
              <div 
                className="absolute"
                style={{
                  left: isDesktop ? 'calc(50% + 335px)' : 'calc(50% + 193px)',
                  top: isDesktop ? 'calc(50% - 280px)' : 'calc(50% - 220px)',
                  width: isDesktop ? '200px' : '135px',
                  height: isDesktop ? '240px' : '162px',
                  backgroundImage: `url('https://customer-assets.emergentagent.com/job_culinary-notes-5/artifacts/ila6g1h4_poloroid-frame.png')`,
                  backgroundSize: 'contain',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'center',
                  transform: isDesktop ? 'rotate(3deg)' : 'rotate(-7deg)',
                  zIndex: 20
                }}
              ></div>
            )}

            {/* Pasta Salad Sticky Note - RIGHT SIDE (KEEP THIS!) */}
            <div 
              className="absolute z-20"
              style={{
                left: isDesktop ? 'calc(50% + 295px)' : 'calc(50% + 150px)',
                top: isDesktop ? 'calc(50% - 50px)' : 'calc(50% + 40px)',
                width: isDesktop ? '270px' : '200px',
                height: isDesktop ? '270px' : '200px',
                backgroundImage: `url('/pasta-salad-note.png')`,
                backgroundSize: 'contain',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
                transform: 'rotate(8deg)',
                display: isMobile ? 'none' : 'block'
              }}
            ></div>
          </div>

          {/* Center Blue Scalloped Paper with Main Message */}
          <div 
            className="relative z-10 text-center w-full mx-4 lg:mx-0"
            style={{
              backgroundImage: isMobile ? `url('https://customer-assets.emergentagent.com/job_recipemanager-1/artifacts/ooxi7c8c_mobile-blue-paper.png')` : (isDesktop ? `url('https://customer-assets.emergentagent.com/job_recipehub-15/artifacts/7n75ztus_blue-paper-wide-desktop.png')` : `url('https://customer-assets.emergentagent.com/job_cookbookai/artifacts/2z47xjwk_tablet%20blue%20paper.png')`),
              backgroundSize: 'contain',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
              minHeight: isMobile ? '690px' : (isDesktop ? '604px' : '540px'),
              maxWidth: isDesktop ? '57.13rem' : (isMobile ? '402px' : '594px'),
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              paddingTop: isDesktop ? '84.92px' : (isMobile ? '60px' : '100px'),
              paddingBottom: isDesktop ? '109.08px' : (isMobile ? '60px' : '100px'),
              paddingLeft: isDesktop ? '65px' : (isMobile ? '50px' : '80px'),
              paddingRight: isDesktop ? '65px' : (isMobile ? '50px' : '80px')
            }}
          >
            <h2 
              className={`font-bold ${isMobile ? '' : 'mb-4 md:mb-6'} ${isDesktop ? 'text-5xl' : 'text-3xl'}`}
              style={{ 
                color: '#51120A',
                fontFamily: "'Instrument Serif', serif",
                lineHeight: '1.2',
                maxWidth: isDesktop ? '60%' : (isMobile ? '100%' : '72%'),
                margin: '0 auto',
                marginBottom: isMobile ? '16px' : undefined,
                letterSpacing: isMobile ? '0' : undefined
              }}
              data-testid="hero-title"
            >
              A place to save, share, and organize{' '}
              <span style={{ whiteSpace: 'nowrap' }}>your recipes</span>
            </h2>
            
            <p 
              className={`${isMobile ? '' : 'mb-6 md:mb-8'} ${isDesktop ? 'text-xl' : 'text-base'}`}
              style={{ 
                color: '#51120A',
                fontFamily: "'Bricolage Grotesque', sans-serif",
                maxWidth: isDesktop ? '60%' : (isMobile ? '100%' : '72%'),
                margin: isMobile ? '0 auto' : '0 auto 2rem auto',
                marginBottom: isMobile ? '16px' : undefined
              }}
              data-testid="hero-subheader"
            >
              Write them, say them, or paste them from somewhere else. Never lose a recipe again.
            </p>

            <div className={`flex gap-3 items-center ${isMobile ? 'flex-col w-full' : 'flex-row'}`} style={{ position: 'relative', zIndex: 30, marginTop: '0' }}>
              <Button 
                size="lg"
                onClick={() => setShowBetaDialog(true)}
                className={`font-medium join-waitlist-btn transition-colors ${isMobile ? 'w-full px-8 py-4 text-lg' : 'px-6 py-3 md:px-8 md:py-4 text-base md:text-lg'} text-white`}
                style={{ 
                  backgroundColor: '#51120A',
                  borderColor: '#51120A',
                  fontFamily: "'Bricolage Grotesque', sans-serif"
                }}
                data-testid="hero-join-beta-btn"
              >
                Join Waitlist
              </Button>
              <Button 
                size="lg"
                onClick={openSignUpDialog}
                variant="outline"
                className={`font-medium hover-blush transition-colors focus:outline-none focus:ring-0 ${isMobile ? 'w-full px-8 py-4 text-lg' : 'px-6 py-3 md:px-8 md:py-4 text-base md:text-lg'}`}
                style={{ 
                  borderColor: '#51120A',
                  color: '#51120A',
                  fontFamily: "'Bricolage Grotesque', sans-serif",
                  borderWidth: '1px'
                }}
                data-testid="hero-signup-btn"
              >
                Sign Up
              </Button>
            </div>

            {/* Mobile Collage - only on mobile */}
            {/* Mobile: Full Collage with Decorative Elements - ORIGINAL POSITION */}
            {isMobile && (
              <div 
                className="absolute z-20"
                style={{
                  bottom: '-15px',
                  left: '50%',
                  transform: `translateX(-50%) translateY(5%)`,
                  width: '389px',
                  height: 'auto'
                }}
              >
                <img 
                  src="https://customer-assets.emergentagent.com/job_culinary-notes-5/artifacts/kkon5j37_mobile-collage.png" 
                  alt="Recipe collage" 
                  style={{ width: '100%', height: 'auto' }}
                />
              </div>
            )}
          </div>

        </div>
      </div>

      {/* Section: "Most of us don't keep track" + Auto-scrolling Carousel */}
      <div
        style={{
          backgroundColor: '#EEE7D8',
          paddingTop: isMobile ? '30px' : '50px',
          paddingBottom: '40px',
          overflow: 'hidden'
        }}
      >
        <div style={{ maxWidth: '800px', margin: '0 auto', textAlign: 'center', padding: '0 20px' }}>
          <h2 style={{
            fontFamily: "'Instrument Serif', serif",
            fontSize: isMobile ? '28px' : (isDesktop ? '40px' : '36px'),
            color: '#51120A',
            lineHeight: '1.3',
            marginBottom: '12px'
          }}>
            Most of us don't actually{' '}<span style={{ fontFamily: "'Gochi Hand', cursive" }}>keep track</span>{!isMobile && <br />}{isMobile && ' '}of the recipes we love
          </h2>
          <p style={{
            fontFamily: "'Bricolage Grotesque', sans-serif",
            fontSize: isMobile ? '14px' : '16px',
            color: '#51120A',
            marginBottom: '48px'
          }}>
            This is how you're currently tracking your recipes...
          </p>
        </div>

        <div style={{ overflow: 'hidden', padding: '20px 0' }}>
          <div className="recipe-carousel-track" style={{
            display: 'flex',
            gap: '24px',
            width: 'max-content'
          }}>
            {[...recipeCards, ...recipeCards, ...recipeCards].map((card, i) => (
              <div key={i} style={{
                flexShrink: 0,
                width: isMobile ? '260px' : '340px',
                height: isMobile ? '163px' : '213px',
                transform: `rotate(${i % 2 === 0 ? '3.5deg' : '-3deg'})`,
                backgroundImage: `url(${cardBackgrounds[card.bg % cardBackgrounds.length]})`,
                backgroundSize: '100% 100%',
                backgroundRepeat: 'no-repeat',
                padding: isMobile ? '20px 50px' : '24px 82px',
                fontFamily: "'Gochi Hand', cursive",
                fontSize: isMobile ? '23px' : '23px',
                fontWeight: '400',
                color: '#51120A',
                lineHeight: '0.89',
                display: 'flex',
                alignItems: 'center',
                textAlign: 'center',
                justifyContent: 'center',
              }}>
                {card.text}
              </div>
            ))}
          </div>
        </div>

        <div style={{ maxWidth: isMobile ? '300px' : '700px', margin: '48px auto 0', textAlign: 'center', padding: '0 20px' }}>
          <p style={{
            fontFamily: "'Gochi Hand', cursive",
            fontSize: isMobile ? '18px' : '22px',
            color: '#51120A',
            lineHeight: '1.4'
          }}>
            Discovery isn't the problem, holding on to a recipe is
          </p>
        </div>
      </div>

      {/* Section: "One home for every recipe you love" + Accordion */}
      <div
        ref={accordionSectionRef}
        style={{
          backgroundColor: '#EEE7D8',
          paddingTop: isMobile ? '60px' : '100px',
          paddingBottom: isMobile ? '60px' : '100px',
          paddingLeft: isMobile ? '20px' : (isDesktop ? '80px' : '60px'),
          paddingRight: isMobile ? '20px' : (isDesktop ? '80px' : '60px')
        }}
      >
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{
            fontFamily: "'Instrument Serif', serif",
            fontSize: isMobile ? '28px' : (isDesktop ? '40px' : '36px'),
            color: '#51120A',
            lineHeight: '1.3',
            textAlign: 'center',
            marginBottom: isMobile ? '40px' : '60px',
            maxWidth: isMobile ? '260px' : 'none',
            margin: isMobile ? '0 auto 40px' : undefined
          }}>
            One home for{' '}
            <span style={{ fontFamily: "'Gochi Hand', cursive" }}>every recipe</span>{' '}
            you love
          </h2>

          <div style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? '40px' : '80px',
            alignItems: isMobile ? 'center' : 'center'
          }}>
            {!isMobile && (
              <div style={{ width: '40%', flexShrink: 0, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <div style={{ position: 'relative', width: '380px', height: '430px' }}>
                  {accordionItems.map((item, index) => (
                    <img
                      key={index}
                      src={item.image}
                      alt={item.title}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        opacity: activeAccordionItem === index ? 1 : 0,
                        transition: 'opacity 0.5s ease',
                      }}
                    />
                  ))}
                </div>
              </div>
            )}

            <div ref={accordionItemsRef} style={{ width: isMobile ? '100%' : '55%', maxWidth: isMobile ? '78%' : '100%', margin: isMobile ? '0 auto' : '0' }} role="region" aria-label="Features">
              {accordionItems.map((item, index) => (
                <div
                  key={index}
                  style={{
                    borderBottom: '1px solid rgba(81, 18, 10, 0.15)',
                    padding: '20px 0'
                  }}
                >
                  <button
                    id={`accordion-header-${index}`}
                    onClick={() => { setActiveAccordionItem(index); setAccordionTimerKey(k => k + 1); }}
                    aria-expanded={activeAccordionItem === index}
                    aria-controls={`accordion-panel-${index}`}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      width: '100%',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: 0,
                      textAlign: 'left'
                    }}
                  >
                    <h3 style={{
                      fontFamily: "'Bricolage Grotesque', sans-serif",
                      fontSize: isMobile ? '16px' : '18px',
                      color: '#51120A',
                      fontWeight: '600'
                    }}>
                      {item.title}
                    </h3>
                    <span aria-hidden="true" style={{
                      fontFamily: "'Bricolage Grotesque', sans-serif",
                      fontSize: '24px',
                      color: '#51120A',
                      transform: activeAccordionItem === index ? 'rotate(45deg)' : 'rotate(0deg)',
                      transition: 'transform 0.3s ease',
                      lineHeight: 1,
                      flexShrink: 0,
                      marginLeft: '16px'
                    }}>
                      +
                    </span>
                  </button>
                  <div
                    id={`accordion-panel-${index}`}
                    role="region"
                    aria-labelledby={`accordion-header-${index}`}
                    style={{
                      maxHeight: activeAccordionItem === index ? (isMobile ? '500px' : '200px') : '0',
                      overflow: 'hidden',
                      transition: 'max-height 0.4s ease'
                    }}
                  >
                    <p style={{
                      fontFamily: "'Bricolage Grotesque', sans-serif",
                      fontSize: isMobile ? '14px' : '16px',
                      color: '#51120A',
                      opacity: 0.8,
                      lineHeight: '1.6',
                      paddingTop: '12px',
                      paddingRight: isMobile ? '0' : '40px'
                    }}>
                      {item.content}
                    </p>
                    {isMobile && activeAccordionItem === index && (
                      <div style={{ display: 'flex', justifyContent: 'center', marginTop: '16px' }}>
                        <img
                          src={item.image}
                          alt={item.title}
                          style={{
                            width: '100%',
                            maxWidth: '280px',
                            height: 'auto',
                            objectFit: 'contain',
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Section: "Organize your meals" + Parallax Category Pills */}
      <div
        ref={categoryPillsRef}
        style={{
          backgroundColor: '#EEE7D8',
          paddingTop: isMobile ? '60px' : '100px',
          paddingBottom: isMobile ? '60px' : '100px',
          overflow: 'hidden'
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: '48px', padding: '0 20px' }}>
          <h2 style={{
            fontFamily: "'Instrument Serif', serif",
            fontSize: isMobile ? '28px' : (isDesktop ? '40px' : '36px'),
            color: '#51120A',
            lineHeight: '1.3'
          }}>
            Organize your meals for<br />
            whatever{' '}
            <span style={{
              fontFamily: "'Gochi Hand', cursive"
            }}>season</span>{' '}
            you're in
          </h2>
        </div>

        <div style={{
          display: 'flex',
          gap: isMobile ? '12px' : '16px',
          marginBottom: isMobile ? '12px' : '16px',
          transform: `translateX(${(categoryScrollOffset - 0.3) * (isMobile ? 150 : 300) - 120}px)`,
          transition: 'transform 0.1s linear',
          width: 'max-content',
        }}>
          {[...topRowPills, ...topRowPills].map((pill, i) => (
            <div key={i} style={{
              padding: isMobile ? '10px 20px' : '12px 28px',
              borderRadius: '30px',
              fontFamily: "'Gochi Hand', cursive",
              fontSize: isMobile ? '16px' : '20px',
              color: '#51120A',
              whiteSpace: 'nowrap',
              backgroundColor: '#C9D7E9'
            }}>
              {pill}
            </div>
          ))}
        </div>

        <div style={{
          display: 'flex',
          gap: isMobile ? '12px' : '16px',
          justifyContent: 'flex-end',
          transform: `translateX(${(0.3 - categoryScrollOffset) * (isMobile ? 150 : 300) + 120}px)`,
          transition: 'transform 0.1s linear',
          width: 'max-content',
          marginLeft: 'auto',
        }}>
          {[...bottomRowPills, ...bottomRowPills].map((pill, i) => (
            <div key={i} style={{
              padding: isMobile ? '10px 20px' : '12px 28px',
              borderRadius: '30px',
              fontFamily: "'Gochi Hand', cursive",
              fontSize: isMobile ? '16px' : '20px',
              color: '#51120A',
              whiteSpace: 'nowrap',
              backgroundColor: '#C9D7E9'
            }}>
              {pill}
            </div>
          ))}
        </div>
      </div>

      {/* How It Works Section */}
      <div 
        style={{ 
          backgroundColor: '#EEE7D8',
          paddingTop: isMobile ? '60px' : (isDesktop ? '80px' : '60px'),
          paddingBottom: isMobile ? '60px' : (isDesktop ? '100px' : '56px'),
          paddingLeft: isMobile ? '20px' : (isDesktop ? '80px' : '60px'),
          paddingRight: isMobile ? '20px' : (isDesktop ? '80px' : '60px')
        }}
      >
        <div className="max-w-7xl mx-auto">
          <div className={`flex ${isMobile ? 'flex-col' : 'flex-row'} items-center ${isMobile ? 'gap-8' : 'gap-16'}`}>
            {/* Left side - Text content */}
            <div className={isMobile ? 'w-full' : 'w-1/2'} style={{ paddingLeft: isDesktop ? '50px' : '0' }}>
              <div style={{ maxWidth: isMobile ? '78%' : '100%', margin: isMobile ? '0 auto' : '0' }}>
                <h2 
                  style={{ 
                    fontFamily: "'Instrument Serif', serif",
                    fontSize: isDesktop ? '40px' : (isMobile ? '32px' : '40px'),
                    color: '#51120A',
                    marginBottom: '24px',
                    lineHeight: '1.2',
                    maxWidth: isDesktop ? '100%' : (isMobile ? '100%' : '70%'),
                    textAlign: isMobile ? 'center' : 'left'
                  }}
                >
                  A sneak peak into{(isDesktop || isMobile) && <br />} how it works!
                </h2>
                
                <p 
                  style={{ 
                    fontFamily: "'Bricolage Grotesque', sans-serif",
                    fontSize: isDesktop ? '18px' : '16px',
                    color: '#51120A',
                    marginBottom: '32px',
                    lineHeight: '1.6',
                    maxWidth: isDesktop ? '80%' : '100%'
                  }}
                >
                  And how it looks! Plus how to add your own recipes, share them, organize them with custom filters, and pin your current favs.
                </p>

                <button 
                onClick={() => setShowVideoModal(true)}
                className="flex items-center gap-3 group"
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
              >
                <img 
                  src="https://customer-assets.emergentagent.com/job_recipe-cards-2/artifacts/gkj117ts_playbutton.png"
                  alt="Play button"
                  className="group-hover:opacity-80 transition-opacity"
                  style={{
                    width: '14px',
                    height: '14px'
                  }}
                />
                
                <span 
                  className="underline group-hover:opacity-80 transition-opacity"
                  style={{ 
                    fontFamily: "'Bricolage Grotesque', sans-serif",
                    fontSize: '18px',
                    color: '#875C66'
                  }}
                >
                  Watch video
                </span>
              </button>
              </div>
            </div>

            {/* Right side - TV image */}
            <div className={isMobile ? 'w-full mt-8' : 'w-1/2'}>
              <img 
                src="https://customer-assets.emergentagent.com/job_808efdc3-e9f7-4232-86e4-0ae7de900168/artifacts/v766cyjb_TV.png"
                alt="Recipe app demonstration"
                onClick={() => setShowVideoModal(true)}
                width={572}
                height={410}
                style={{ 
                  width: '100%', 
                  height: 'auto',
                  aspectRatio: '572 / 410',
                  maxWidth: isDesktop ? '450px' : '100%',
                  margin: '0 auto',
                  display: 'block',
                  cursor: 'pointer',
                  transition: 'opacity 0.2s'
                }}
                onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.8')}
                onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Meet the Founder Section */}
      <div 
        ref={founderSectionRef}
        style={{ 
          backgroundColor: '#EEE7D8',
          paddingTop: isMobile ? '60px' : (isDesktop ? '0' : '60px'),
          paddingBottom: isMobile ? '20px' : '0',
          paddingLeft: isMobile ? '20px' : (isDesktop ? '80px' : '60px'),
          paddingRight: isMobile ? '20px' : (isDesktop ? '80px' : '60px'),
          marginTop: isMobile ? '0px' : '-30px',
          minHeight: isDesktop ? '400vh' : 'auto',
          position: 'relative'
        }}
      >
        {/* Mobile version - simple stack */}
        {isMobile && (
          <div className="space-y-8">
            <h2 
              className="text-center"
              style={{ 
                fontFamily: "'Instrument Serif', serif",
                fontSize: '32px',
                color: '#51120A',
                lineHeight: '1.2',
                marginBottom: '40px'
              }}
            >
              Meet the Founder
            </h2>
            
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '40px' }}>
              <img 
                src="https://customer-assets.emergentagent.com/job_recipemanager-1/artifacts/52zlka5f_founder-photo.png"
                alt="Brittany - Founder"
                width={489}
                height={552}
                style={{ width: '100%', maxWidth: '400px', height: 'auto', aspectRatio: '489 / 552' }}
              />
            </div>

            <div className="space-y-12" style={{ maxWidth: '78%', margin: '0 auto' }}>
              <div>
                <h3 style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: '20px', color: '#51120A', marginBottom: '16px', fontWeight: '600' }}>
                  Where it all began
                </h3>
                <p style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: '16px', color: '#51120A', lineHeight: '1.8' }}>
                  Hi, I'm Brittany – entrepreneur, home cook, creative director, soon-to-be mom, and the heart behind Wellby.<br /><br />
                  I've been cooking most of my meals at home since college, when I escaped the dining hall and started calling my mom for every recipe she ever made for me. Over the years, cooking became more than a hobby – especially as I learned through my own health journey that what we eat impacts how we feel. Being in the kitchen became grounding, creative, and one of the biggest ways I support my health and everyday life.
                </p>
              </div>

              <div>
                <h3 style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: '20px', color: '#51120A', marginBottom: '16px', fontWeight: '600' }}>
                  My frustrations
                </h3>
                <p style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: '16px', color: '#51120A', lineHeight: '1.8' }}>
                  The only problem? I never had a consistent way to keep track of anything I made. Instagram phases came and went. Recipe cards took too much upkeep. My Notes app got messy. Other recipe apps were ugly or missing features I needed. I'd plan a week of meals, fill my fridge, and then forget what half the ingredients were for.
                </p>
              </div>

              <div>
                <h3 style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: '20px', color: '#51120A', marginBottom: '16px', fontWeight: '600' }}>
                  Where Wellby comes in
                </h3>
                <p style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: '16px', color: '#51120A', lineHeight: '1.8' }}>
                  So I dreamt up Wellby — a place that feels like my kitchen: warm lighting, candles, flowers, handwritten menus. A keepsake for the meals that nourish and bring people together, and a simple way to share them, too. Building this has given me so much energy and joy. I'm so excited to share it with you, and I hope Wellby becomes one of your favorite little corners of the internet.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Tablet version - sticky image left, scrolling text right */}
        {!isMobile && !isDesktop && (
          <div style={{ paddingTop: '80px' }}>
            <h2 
              className="text-center"
              style={{ 
                fontFamily: "'Instrument Serif', serif",
                fontSize: '40px',
                color: '#51120A',
                lineHeight: '1.2',
                marginBottom: '60px'
              }}
            >
              Meet the Founder
            </h2>

            <div style={{ display: 'flex', gap: '50px', alignItems: 'flex-start' }}>
              <div style={{ width: '40%', flexShrink: 0, position: 'sticky', top: '120px' }}>
                <img 
                  src="https://customer-assets.emergentagent.com/job_recipemanager-1/artifacts/52zlka5f_founder-photo.png"
                  alt="Brittany - Founder"
                  style={{ width: '100%', height: 'auto', display: 'block' }}
                />
              </div>

              <div className="space-y-12" style={{ width: '60%' }}>
                <div>
                  <h3 style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: '22px', color: '#51120A', marginBottom: '16px', fontWeight: '600' }}>
                    Where it all began
                  </h3>
                  <p style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: '16px', color: '#51120A', lineHeight: '1.8' }}>
                    Hi, I'm Brittany – entrepreneur, home cook, creative director, soon-to-be mom, and the heart behind Wellby.<br /><br />
                    I've been cooking most of my meals at home since college, when I escaped the dining hall and started calling my mom for every recipe she ever made for me. Over the years, cooking became more than a hobby – especially as I learned through my own health journey that what we eat impacts how we feel. Being in the kitchen became grounding, creative, and one of the biggest ways I support my health and everyday life.
                  </p>
                </div>

                <div>
                  <h3 style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: '22px', color: '#51120A', marginBottom: '16px', fontWeight: '600' }}>
                    My frustrations
                  </h3>
                  <p style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: '16px', color: '#51120A', lineHeight: '1.8' }}>
                    The only problem? I never had a consistent way to keep track of anything I made. Instagram phases came and went. Recipe cards took too much upkeep. My Notes app got messy. Other recipe apps were ugly or missing features I needed. I'd plan a week of meals, fill my fridge, and then forget what half the ingredients were for.
                  </p>
                </div>

                <div>
                  <h3 style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: '22px', color: '#51120A', marginBottom: '16px', fontWeight: '600' }}>
                    Where Wellby comes in
                  </h3>
                  <p style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: '16px', color: '#51120A', lineHeight: '1.8' }}>
                    So I dreamt up Wellby — a place that feels like my kitchen: warm lighting, candles, flowers, handwritten menus. A keepsake for the meals that nourish and bring people together, and a simple way to share them, too. Building this has given me so much energy and joy. I'm so excited to share it with you, and I hope Wellby becomes one of your favorite little corners of the internet.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Desktop version - advanced scroll animation */}
        {isDesktop && (
          <div className="max-w-7xl mx-auto" style={{ position: 'relative' }}>
            {/* Sticky container for title and image */}
            <div
              style={{
                position: 'sticky',
                top: '80px',
                paddingTop: '80px',
                zIndex: 5
              }}
            >
              {/* Title */}
              <h2 
                className="text-center"
                style={{ 
                  fontFamily: "'Instrument Serif', serif",
                  fontSize: isDesktop ? '40px' : (isMobile ? '32px' : '40px'),
                  color: '#51120A',
                  lineHeight: '1.2',
                  marginBottom: '84px'
                }}
              >
                Meet the Founder
              </h2>

              {/* Content row - image and text side by side */}
              <div style={{ display: 'flex', gap: '80px', alignItems: 'center', justifyContent: 'center' }}>
                {/* Image - fixed width */}
                <div style={{ width: '450px', flexShrink: 0 }}>
                  <img 
                    src="https://customer-assets.emergentagent.com/job_recipemanager-1/artifacts/52zlka5f_founder-photo.png"
                    alt="Brittany - Founder"
                    style={{ width: '100%', height: 'auto', display: 'block' }}
                  />
                </div>

                {/* Text content - transitions between 3 paragraphs */}
                <div style={{ width: '500px', position: 'relative', minHeight: '400px' }}>
                  {/* Paragraph 1 */}
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      opacity: scrollProgress < 0.15 ? 1 : Math.max(0, 1 - (scrollProgress - 0.15) * 10),
                      pointerEvents: scrollProgress > 0.25 ? 'none' : 'auto'
                    }}
                  >
                    <h3 style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: '24px', color: '#51120A', marginBottom: '24px', fontWeight: '600' }}>
                      Where it all began
                    </h3>
                    <p style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: '18px', color: '#51120A', lineHeight: '1.8' }}>
                      Hi, I'm Brittany – entrepreneur, home cook, creative director, soon-to-be mom, and the heart behind Wellby.<br /><br />
                      I've been cooking most of my meals at home since college, when I escaped the dining hall and started calling my mom for every recipe she ever made for me. Over the years, cooking became more than a hobby – especially as I learned through my own health journey that what we eat impacts how we feel. Being in the kitchen became grounding, creative, and one of the biggest ways I support my health and everyday life.
                    </p>
                  </div>

                  {/* Paragraph 2 */}
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      opacity: scrollProgress < 0.2 ? 0 : scrollProgress < 0.3 ? (scrollProgress - 0.2) * 10 : scrollProgress < 0.45 ? 1 : Math.max(0, 1 - (scrollProgress - 0.45) * 10),
                      pointerEvents: (scrollProgress < 0.2 || scrollProgress > 0.55) ? 'none' : 'auto'
                    }}
                  >
                    <h3 style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: '24px', color: '#51120A', marginBottom: '24px', fontWeight: '600' }}>
                      My frustrations
                    </h3>
                    <p style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: '18px', color: '#51120A', lineHeight: '1.8' }}>
                      The only problem? I never had a consistent way to keep track of anything I made. Instagram phases came and went. Recipe cards took too much upkeep. My Notes app got messy. Other recipe apps were ugly or missing features I needed. I'd plan a week of meals, fill my fridge, and then forget what half the ingredients were for.
                    </p>
                  </div>

                  {/* Paragraph 3 */}
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      opacity: scrollProgress < 0.5 ? 0 : Math.min(1, (scrollProgress - 0.5) * 10),
                      pointerEvents: scrollProgress < 0.5 ? 'none' : 'auto'
                    }}
                  >
                    <h3 style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: '24px', color: '#51120A', marginBottom: '24px', fontWeight: '600' }}>
                      Where Wellby comes in
                    </h3>
                    <p style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: '18px', color: '#51120A', lineHeight: '1.8' }}>
                      So I dreamt up Wellby — a place that feels like my kitchen: warm lighting, candles, flowers, handwritten menus. A keepsake for the meals that nourish and bring people together, and a simple way to share them, too. Building this has given me so much energy and joy. I'm so excited to share it with you, and I hope Wellby becomes one of your favorite little corners of the internet.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Spacer to enable scrolling */}
            <div style={{ height: '300vh' }} />
          </div>
        )}
      </div>

      {/* Outro CTA Section - Brown Paper */}
      <div
        style={{
          backgroundColor: '#EEE7D8',
          paddingTop: isMobile ? '10px' : (isDesktop ? '100px' : '70px'),
          paddingBottom: isMobile ? '10px' : (isDesktop ? '100px' : '70px'),
          paddingLeft: isMobile ? '20px' : (isDesktop ? '80px' : '60px'),
          paddingRight: isMobile ? '20px' : (isDesktop ? '80px' : '60px'),
          marginTop: isDesktop ? '-288px' : (isMobile ? '0px' : '-60px'),
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <div
          style={{
            backgroundImage: isMobile 
              ? `url('https://customer-assets.emergentagent.com/job_cookbookai/artifacts/1btd52he_brown-ripped%20paper-mobile.png')`
              : (isDesktop 
                ? `url('https://customer-assets.emergentagent.com/job_cookbookai/artifacts/rq0c5btw_brown-ripped%20paper%20desktop.png')`
                : `url('https://customer-assets.emergentagent.com/job_cookbookai/artifacts/dexlv1hx_brown-ripped%20paper-tablet.png')`),
            backgroundSize: 'contain',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
            width: '100%',
            maxWidth: isDesktop ? '1265px' : (isMobile ? '400px' : '690px'),
            minHeight: isDesktop ? '805px' : (isMobile ? '575px' : '690px'),
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: isDesktop ? '115px 92px' : (isMobile ? '69px 46px' : '92px 69px'),
            filter: isMobile ? 'none' : undefined,
            boxShadow: isMobile ? 'none' : undefined,
            WebkitFilter: isMobile ? 'none' : undefined
          }}
        >
          {/* Headline */}
          <h2
            style={{
              fontFamily: "'Instrument Serif', serif",
              fontSize: isDesktop ? '48px' : (isMobile ? '36px' : '48px'),
              color: '#EEE7D8',
              lineHeight: '1.2',
              textAlign: 'center',
              marginBottom: isDesktop ? '48px' : '32px',
              maxWidth: isDesktop ? '700px' : '100%',
              fontWeight: 'normal'
            }}
          >
            Your recipes deserve a home
          </h2>

          {/* Buttons */}
          <div className={`flex gap-4 items-center ${isMobile ? 'flex-col w-full' : 'flex-row'}`}>
            <Button
              size="lg"
              onClick={() => setShowBetaDialog(true)}
              className={`font-medium join-waitlist-cream transition-colors ${isMobile ? 'w-full px-8 py-4 text-lg' : 'px-8 py-4 text-lg'}`}
              style={{
                backgroundColor: '#EEE7D8',
                color: '#51120A',
                borderColor: '#EEE7D8',
                fontFamily: "'Bricolage Grotesque', sans-serif"
              }}
            >
              Join Waitlist
            </Button>
            <Button
              size="lg"
              onClick={openSignUpDialog}
              variant="outline"
              className={`font-medium transition-colors focus:outline-none focus:ring-0 ${isMobile ? 'w-full px-8 py-4 text-lg' : 'px-8 py-4 text-lg'}`}
              style={{
                backgroundColor: 'transparent',
                color: '#EEE7D8',
                borderColor: '#EEE7D8',
                borderWidth: '1px',
                fontFamily: "'Bricolage Grotesque', sans-serif"
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#86656C';
                e.currentTarget.style.color = 'white';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#EEE7D8';
              }}
            >
              Sign up
            </Button>
          </div>
        </div>
      </div>

      {/* Sign Up Dialog */}
      <Dialog open={showSignUpDialog} onOpenChange={(open) => {
        setShowSignUpDialog(open);
        if (!open) setSignUpStep(inviteMode || inviteLookupPending ? 2 : 1);
      }}>
        <DialogContent data-testid="landing-signup-dialog" style={{ backgroundColor: '#F3EEE4', border: '1px solid #D6D0C2', boxShadow: 'none' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Instrument Serif', serif", fontSize: '28px', color: '#52120A' }}>Sign Up</DialogTitle>
            <DialogDescription style={{ fontFamily: "'Bricolage Grotesque', sans-serif", color: '#52120A' }}>
              {signUpStep === 1 ? 'Enter your invite code to get started' :
               signUpStep === 2 ? 'Choose how you\'d like to create your account' :
               'Complete your account setup'}
            </DialogDescription>
          </DialogHeader>
          {signUpStep === 1 && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="accessCode" style={{ fontFamily: "'Bricolage Grotesque', sans-serif", color: '#52120A' }}>Invite Code</Label>
                <Input
                  id="accessCode"
                  type="text"
                  value={formData.accessCode || ''}
                  onChange={(e) => setFormData({ ...formData, accessCode: e.target.value })}
                  data-testid="landing-accesscode-input"
                  placeholder="Enter your invite code"
                  style={{ borderColor: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif", height: '48px', fontSize: '16px' }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && formData.accessCode) {
                      e.preventDefault();
                      setSignUpStep(2);
                    }
                  }}
                />
                <p className="text-xs mt-1" style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif", opacity: 0.7 }}>
                  Don't have a code?{' '}
                  <button
                    type="button"
                    onClick={() => {
                      setShowSignUpDialog(false);
                      setSignUpStep(1);
                      setShowBetaDialog(true);
                    }}
                    className="underline hover:opacity-70"
                    style={{ 
                      color: '#51120A',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      fontFamily: "'Bricolage Grotesque', sans-serif"
                    }}
                  >
                    Fill out 'Join Waitlist'
                  </button>{' '}
                  and you'll receive one if there's an opening.
                </p>
              </div>
              <Button
                type="button"
                className="w-full"
                disabled={!formData.accessCode}
                onClick={() => setSignUpStep(2)}
                style={{ backgroundColor: '#52120A', borderColor: '#52120A', color: 'white', fontFamily: "'Bricolage Grotesque', sans-serif", height: '48px', fontSize: '16px' }}
              >
                Continue
              </Button>
            </div>
          )}

          {signUpStep === 2 && (
            <div className="space-y-4">
              {googleClientId && !inviteLookupPending && (
                <>
                  <div style={{ position: 'relative', overflow: 'hidden', height: '48px' }}>
                    <button
                      type="button"
                      onClick={() => triggerHiddenGoogleBtn(hiddenGoogleBtnRef)}
                      className="w-full flex items-center justify-center gap-3 rounded-md transition-opacity hover:opacity-90"
                      style={{
                        backgroundColor: '#52120A',
                        color: 'white',
                        fontFamily: "'Bricolage Grotesque', sans-serif",
                        border: 'none',
                        cursor: 'pointer',
                        height: '48px',
                        fontSize: '16px',
                      }}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="white"/>
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="white"/>
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="white"/>
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="white"/>
                      </svg>
                      Sign up with Google
                    </button>
                    <div ref={hiddenGoogleBtnRef} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, zIndex: 1 }} />
                  </div>
                  <div className="flex items-center gap-3 my-1">
                    <div className="flex-1 h-px" style={{ backgroundColor: '#51120A', opacity: 0.2 }} />
                    <span style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: '13px' }}>or</span>
                    <div className="flex-1 h-px" style={{ backgroundColor: '#51120A', opacity: 0.2 }} />
                  </div>
                </>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label style={{ fontFamily: "'Bricolage Grotesque', sans-serif", color: '#52120A' }}>First Name</Label>
                  <Input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => {
                      setFormData({ ...formData, firstName: e.target.value });
                    }}
                    data-testid="landing-firstname-input"
                    style={{ borderColor: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif", height: '48px', fontSize: '16px' }}
                  />
                </div>
                <div>
                  <Label style={{ fontFamily: "'Bricolage Grotesque', sans-serif", color: '#52120A' }}>Last Name</Label>
                  <Input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => {
                      setFormData({ ...formData, lastName: e.target.value });
                    }}
                    data-testid="landing-lastname-input"
                    style={{ borderColor: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif", height: '48px', fontSize: '16px' }}
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="signupEmail" style={{ fontFamily: "'Bricolage Grotesque', sans-serif", color: '#52120A' }}>Email</Label>
                <Input
                  id="signupEmail"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  disabled={inviteMode || inviteLookupPending}
                  placeholder={inviteLookupPending ? "Loading invited email…" : undefined}
                  aria-busy={inviteLookupPending}
                  aria-describedby={inviteLookupPending ? "invite-email-status" : undefined}
                  data-testid="landing-email-input"
                  style={{ borderColor: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif", height: '48px', fontSize: '16px', ...(inviteMode || inviteLookupPending ? { opacity: 0.75, backgroundColor: '#E5DECB' } : {}) }}
                />
                {inviteLookupPending ? (
                  <p id="invite-email-status" role="status" className="text-xs mt-1" style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>
                    Checking your invitation…
                  </p>
                ) : inviteMode && (
                  <p className="text-xs mt-1" style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif", opacity: 0.7 }}>
                    Your invitation is tied to this email address.
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="signupUsername" style={{ fontFamily: "'Bricolage Grotesque', sans-serif", color: '#52120A' }}>Username</Label>
                <Input
                  id="signupUsername"
                  type="text"
                  value={formData.username}
                  onChange={(e) => {
                    const username = e.target.value.toLowerCase().replace(/\s+/g, '-');
                    setFormData({ ...formData, username });
                  }}
                  data-testid="landing-username-input"
                  placeholder="yourusername"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  style={{
                    borderColor: (usernameStatus.state === 'invalid' || usernameStatus.state === 'taken') ? '#DC2626' : '#51120A',
                    fontFamily: "'Bricolage Grotesque', sans-serif", height: '48px', fontSize: '16px'
                  }}
                />
                <p className="text-xs mt-1" data-testid="landing-username-hint" style={{
                  color: (usernameStatus.state === 'invalid' || usernameStatus.state === 'taken') ? '#DC2626' : '#51120A',
                  fontFamily: "'Bricolage Grotesque', sans-serif",
                  opacity: (usernameStatus.state === 'invalid' || usernameStatus.state === 'taken') ? 1 : 0.7
                }}>
                  {usernameStatus.state === 'invalid' || usernameStatus.state === 'taken'
                    ? usernameStatus.reason
                    : usernameStatus.state === 'checking'
                      ? 'Checking availability...'
                      : usernameStatus.state === 'available'
                        ? `Available — your page will be getwellby.com/${formData.username.trim().toLowerCase()}`
                        : 'This becomes your page link: getwellby.com/yourusername'}
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                className="w-full"
                disabled={inviteLookupPending || !formData.firstName || !formData.lastName || !formData.email || !formData.username || usernameStatus.state === 'invalid' || usernameStatus.state === 'taken' || usernameStatus.state === 'checking'}
                onClick={() => setSignUpStep(3)}
                style={{
                  backgroundColor: 'transparent',
                  borderColor: '#51120A',
                  borderWidth: '1.5px',
                  color: '#51120A',
                  fontFamily: "'Bricolage Grotesque', sans-serif",
                  height: '48px',
                  fontSize: '16px',
                }}
              >
                Sign up with email
              </Button>
            </div>
          )}

          {signUpStep === 3 && (
            <form onSubmit={handleSignUp} className="space-y-4">
              <div className="text-sm" style={{ fontFamily: "'Bricolage Grotesque', sans-serif", color: '#52120A' }}>
                Signing up as <strong>{formData.firstName} {formData.lastName}</strong> ({formData.email})
              </div>
              <div>
                <Label htmlFor="password" style={{ fontFamily: "'Bricolage Grotesque', sans-serif", color: '#52120A' }}>Password</Label>
                <PasswordInput
                  id="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  data-testid="landing-password-input"
                  style={{ borderColor: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif", height: '48px', fontSize: '16px' }}
                />
              </div>
              <div>
                <Label htmlFor="confirmPassword" style={{ fontFamily: "'Bricolage Grotesque', sans-serif", color: '#52120A' }}>Confirm Password</Label>
                <PasswordInput
                  id="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  required
                  data-testid="landing-confirm-password-input"
                  style={{ borderColor: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif", height: '48px', fontSize: '16px' }}
                />
              </div>
              <Button 
                type="submit" 
                className="w-full" 
                data-testid="landing-signup-submit"
                disabled={inviteLookupPending}
                style={{ backgroundColor: '#52120A', borderColor: '#52120A', color: 'white', fontFamily: "'Bricolage Grotesque', sans-serif", height: '48px', fontSize: '16px' }}
              >
                Sign Up
              </Button>
            </form>
          )}

          <div className="text-center text-sm mt-2">
            <span style={{ color: '#52120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>Already have an account?</span>{" "}
            <button
              type="button"
              onClick={() => {
                setShowSignUpDialog(false);
                setSignUpStep(inviteMode || inviteLookupPending ? 2 : 1);
                setShowLoginDialog(true);
              }}
              className="underline hover:no-underline"
              data-testid="switch-to-login-link"
              style={{ color: '#52120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}
            >
              Login here
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Choose Handle Dialog (mandatory for new Google signups) */}
      <Dialog open={showChooseHandleDialog} onOpenChange={() => { /* mandatory step — no dismissing */ }}>
        <DialogContent data-testid="choose-handle-dialog" style={{ backgroundColor: '#F3EEE4', border: '1px solid #D6D0C2', boxShadow: 'none' }} onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Instrument Serif', serif", fontSize: '28px', color: '#52120A' }}>Choose your username</DialogTitle>
            <DialogDescription style={{ fontFamily: "'Bricolage Grotesque', sans-serif", color: '#52120A' }}>
              This becomes your page link: getwellby.com/yourusername
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="chooseHandle" style={{ fontFamily: "'Bricolage Grotesque', sans-serif", color: '#52120A' }}>Username</Label>
              <Input
                id="chooseHandle"
                type="text"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                data-testid="choose-handle-input"
                placeholder="yourusername"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                style={{
                  borderColor: (usernameStatus.state === 'invalid' || usernameStatus.state === 'taken') ? '#DC2626' : '#51120A',
                  fontFamily: "'Bricolage Grotesque', sans-serif", height: '48px', fontSize: '16px'
                }}
              />
              <p className="text-xs mt-1" data-testid="choose-handle-hint" style={{
                color: (usernameStatus.state === 'invalid' || usernameStatus.state === 'taken') ? '#DC2626' : '#51120A',
                fontFamily: "'Bricolage Grotesque', sans-serif",
                opacity: (usernameStatus.state === 'invalid' || usernameStatus.state === 'taken') ? 1 : 0.7
              }}>
                {usernameStatus.state === 'invalid' || usernameStatus.state === 'taken'
                  ? usernameStatus.reason
                  : usernameStatus.state === 'checking'
                    ? 'Checking availability...'
                    : usernameStatus.state === 'available'
                      ? `Available — your page will be getwellby.com/${formData.username.trim().toLowerCase()}`
                      : 'Lowercase letters, numbers, underscores, and hyphens'}
              </p>
            </div>
            <Button
              type="button"
              className="w-full"
              data-testid="choose-handle-save"
              disabled={savingHandle || !formData.username || usernameStatus.state !== 'available'}
              onClick={handleSaveChosenHandle}
              style={{ backgroundColor: '#52120A', borderColor: '#52120A', color: 'white', fontFamily: "'Bricolage Grotesque', sans-serif", height: '48px', fontSize: '16px' }}
            >
              {savingHandle ? 'Saving...' : 'Continue'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Login Dialog */}
      <Dialog open={showLoginDialog} onOpenChange={setShowLoginDialog}>
        <DialogContent data-testid="landing-login-dialog" style={{ backgroundColor: '#F3EEE4', border: '1px solid #D6D0C2', boxShadow: 'none' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Instrument Serif', serif", fontSize: '28px', color: '#52120A' }}>Login</DialogTitle>
            <DialogDescription style={{ fontFamily: "'Bricolage Grotesque', sans-serif", color: '#52120A' }}>Welcome back! Please login to your account</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Label htmlFor="loginEmail" style={{ fontFamily: "'Bricolage Grotesque', sans-serif", color: '#52120A' }}>Email</Label>
              <Input
                id="loginEmail"
                type="email"
                value={loginData.email}
                onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                required
                data-testid="login-email-input"
                style={{ borderColor: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif", height: '48px', fontSize: '16px' }}
              />
            </div>
            <div>
              <Label htmlFor="loginPassword" style={{ fontFamily: "'Bricolage Grotesque', sans-serif", color: '#52120A' }}>Password</Label>
              <PasswordInput
                id="loginPassword"
                value={loginData.password}
                onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                required
                data-testid="login-password-input"
                style={{ borderColor: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif", height: '48px', fontSize: '16px' }}
              />
            </div>
            <Button 
              type="submit" 
              className="w-full"
              data-testid="landing-login-submit"
              style={{ backgroundColor: '#52120A', borderColor: '#52120A', color: 'white', fontFamily: "'Bricolage Grotesque', sans-serif", height: '48px', fontSize: '16px' }}
            >
              Login
            </Button>
          </form>
          {googleClientId && (
            <>
              <div className="flex items-center gap-3 my-2">
                <div className="flex-1 h-px" style={{ backgroundColor: '#51120A', opacity: 0.2 }} />
                <span style={{ color: '#51120A', fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: '13px' }}>or</span>
                <div className="flex-1 h-px" style={{ backgroundColor: '#51120A', opacity: 0.2 }} />
              </div>
              <div style={{ position: 'relative', overflow: 'hidden', height: '48px' }}>
                <button
                  type="button"
                  onClick={() => triggerHiddenGoogleBtn(hiddenGoogleLoginBtnRef)}
                  className="w-full flex items-center justify-center gap-3 rounded-md transition-opacity hover:opacity-90"
                  style={{
                    backgroundColor: '#52120A',
                    color: 'white',
                    fontFamily: "'Bricolage Grotesque', sans-serif",
                    border: 'none',
                    cursor: 'pointer',
                    height: '48px',
                    fontSize: '16px',
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="white"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="white"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="white"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="white"/>
                  </svg>
                  Continue with Google
                </button>
                <div ref={hiddenGoogleLoginBtnRef} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, zIndex: 1 }} />
              </div>
            </>
          )}
          <div className="text-center text-sm mt-2">
            <button
              type="button"
              onClick={() => {
                setShowLoginDialog(false);
                setShowForgotPasswordDialog(true);
              }}
              className="underline hover:no-underline"
              style={{ color: '#52120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}
            >
              Forgot password?
            </button>
          </div>
          <div className="text-center text-sm mt-4">
            <span style={{ color: '#52120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}>Don't have an account?</span>{" "}
            <button
              type="button"
              onClick={() => {
                setShowLoginDialog(false);
                openSignUpDialog();
              }}
              className="underline hover:no-underline"
              data-testid="switch-to-signup-link"
              style={{ color: '#52120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}
            >
              Sign up here
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Forgot Password Dialog */}
      <Dialog open={showForgotPasswordDialog} onOpenChange={setShowForgotPasswordDialog}>
        <DialogContent style={{ backgroundColor: '#F3EEE4', border: '1px solid #D6D0C2', boxShadow: 'none' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Instrument Serif', serif", color: '#52120A' }}>Forgot Password</DialogTitle>
            <DialogDescription style={{ fontFamily: "'Bricolage Grotesque', sans-serif", color: '#52120A' }}>
              Enter your email address and we'll send you a link to reset your password
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleForgotPassword} className="space-y-4">
            <div>
              <Label htmlFor="forgotEmail" style={{ fontFamily: "'Bricolage Grotesque', sans-serif", color: '#52120A' }}>Email</Label>
              <Input
                id="forgotEmail"
                type="email"
                value={forgotPasswordEmail}
                onChange={(e) => setForgotPasswordEmail(e.target.value)}
                required
                style={{ borderColor: '#52120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}
              />
            </div>
            <Button 
              type="submit" 
              className="w-full px-8 py-4 text-lg"
              style={{ backgroundColor: '#52120A', borderColor: '#52120A', color: 'white', fontFamily: "'Bricolage Grotesque', sans-serif" }}
            >
              Send Reset Link
            </Button>
          </form>
          <div className="text-center text-sm mt-4">
            <button
              type="button"
              onClick={() => {
                setShowForgotPasswordDialog(false);
                setShowLoginDialog(true);
              }}
              className="underline hover:no-underline"
              style={{ color: '#52120A', fontFamily: "'Bricolage Grotesque', sans-serif" }}
            >
              Back to login
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Email Verification Dialog */}
      <Dialog open={showVerificationDialog} onOpenChange={setShowVerificationDialog}>
        <DialogContent style={{ backgroundColor: '#F3EEE4', border: '1px solid #D6D0C2', boxShadow: 'none' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Instrument Serif', serif", color: '#51120A', fontSize: '28px' }}>
              {verificationDeliveryFailed ? "We couldn’t send your code" : "Check your email"}
            </DialogTitle>
            <DialogDescription style={{ fontFamily: "'Bricolage Grotesque', sans-serif", color: '#51120A' }}>
              {verificationDeliveryFailed
                ? <>Please use Resend below to send a code to <strong>{verificationEmail}</strong></>
                : <>We sent a 6-digit code to <strong>{verificationEmail}</strong></>}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleVerifyCode} className="space-y-4 mt-4">
            <div>
              <Label style={{ fontFamily: "'Bricolage Grotesque', sans-serif", color: '#51120A', marginBottom: '12px', display: 'block' }}>
                Enter 6 digit code
              </Label>
              <div className="flex justify-center gap-2">
                {[0, 1, 2, 3, 4, 5].map((index) => (
                  <input
                    key={index}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={verificationCode[index] || ''}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '');
                      if (value) {
                        const newCode = verificationCode.split('');
                        newCode[index] = value;
                        setVerificationCode(newCode.join(''));
                        // Auto-focus next input
                        if (index < 5 && e.target.nextElementSibling) {
                          e.target.nextElementSibling.focus();
                        }
                      }
                    }}
                    onKeyDown={(e) => {
                      // Handle backspace
                      if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
                        const prevInput = e.target.previousElementSibling;
                        if (prevInput) {
                          prevInput.focus();
                          const newCode = verificationCode.split('');
                          newCode[index - 1] = '';
                          setVerificationCode(newCode.join(''));
                        }
                      }
                    }}
                    onPaste={(e) => {
                      e.preventDefault();
                      const paste = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
                      setVerificationCode(paste);
                    }}
                    style={{ 
                      width: '48px',
                      height: '56px',
                      border: '2px solid #D6D0C2',
                      borderRadius: '8px',
                      fontFamily: "'Bricolage Grotesque', sans-serif",
                      fontSize: '24px',
                      textAlign: 'center',
                      backgroundColor: '#F3EEE4',
                      color: '#51120A',
                      outline: 'none'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#51120A'}
                    onBlur={(e) => e.target.style.borderColor = '#D6D0C2'}
                    data-testid={`verification-code-input-${index}`}
                  />
                ))}
              </div>
            </div>
            <Button
              type="submit"
              className="w-full"
              style={{ backgroundColor: '#51120A', color: 'white', fontFamily: "'Bricolage Grotesque', sans-serif" }}
              data-testid="verify-code-button"
            >
              Verify
            </Button>
            <p 
              className="text-center text-sm"
              style={{ fontFamily: "'Bricolage Grotesque', sans-serif", color: '#51120A' }}
            >
              Didn't receive the code?{' '}
              <button
                type="button"
                onClick={handleResendCode}
                style={{ color: '#51120A', textDecoration: 'underline', background: 'none', border: 'none', cursor: 'pointer', fontWeight: '600' }}
              >
                Resend
              </button>
            </p>
          </form>
        </DialogContent>
      </Dialog>

      {/* Beta Form Dialog */}
      <BetaForm open={showBetaDialog} onOpenChange={setShowBetaDialog} />

      {/* Video Modal */}
      <Dialog open={showVideoModal} onOpenChange={setShowVideoModal}>
        <DialogContent 
          mobileGutters={false}
          className="max-w-4xl w-full p-0"
          style={{ backgroundColor: '#000' }}
        >
          <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden' }}>
            <iframe
              src="https://www.youtube.com/embed/YHLswA0P9jU"
              title="Wellby Demo Video"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                border: 'none'
              }}
            />
          </div>
        </DialogContent>
      </Dialog>

      <LandingFooter style={{ marginTop: isDesktop ? '-150px' : '0' }} />
    </div>
  );
};

export default LandingPage;
